import React from 'react';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';

const LeadTable = ({
  leads = [],
  selectedLeads = [],
  onLeadSelect,
  onSelectAll,
  onViewLead,
  onEditLead,
  onDeleteLead,
  onMoveToATS,
  telecallers = []
}) => {
  const getStatusColor = (status) => {
    const colors = {
      new: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
      contacted: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
      validated: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
      shortlisted: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
      sent_to_client: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300',
      placed: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300',
      rejected: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
      moved_to_ats: 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-300'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
  };

  const getSourceIcon = (source) => {
    const icons = {
      direct_contact: 'Phone',
      job_portal: 'Globe',
      referral: 'Users',
      website: 'Monitor',
      social_media: 'Share2'
    };
    return icons[source] || 'User';
  };

  const formatStatus = (status) => {
    return status?.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ') || '';
  };

  const formatSource = (source) => {
    return source?.split('_').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ') || '';
  };

  const getTelecallerName = (telecallerId) => {
    const telecaller = telecallers?.find(t => t.id === telecallerId);
    return telecaller?.full_name || 'Unassigned';
  };

  const getFileUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http')) return path;
    const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/erp/backend/api';
    const backendRoot = API_BASE_URL.replace(/\/api\/?$/, '');
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    return `${backendRoot}${cleanPath}`;
  };

  // Helper function to parse skills (handle both JSON string and array)
  const parseSkills = (skills) => {
    if (!skills) return [];
    if (Array.isArray(skills)) return skills;
    if (typeof skills === 'string') {
      try {
        const parsed = JSON.parse(skills);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        // If not valid JSON, try splitting by comma
        return skills.split(',').map(s => s.trim()).filter(Boolean);
      }
    }
    return [];
  };

  const allSelected = leads.length > 0 && leads.every(lead => selectedLeads.includes(lead.id));
  const someSelected = leads.some(lead => selectedLeads.includes(lead.id));

  if (leads.length === 0) {
    return (
      <div className="p-8 text-center">
        <Icon name="Users" size={48} className="text-muted-foreground mx-auto mb-4" />
        <h3 className="text-lg font-medium text-foreground mb-2">No leads found</h3>
        <p className="text-muted-foreground">
          Try adjusting your filters or add new leads to get started.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden">
      {/* Desktop Table View */}
      <div className="hidden lg:block overflow-x-auto">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-muted/50">
            <tr>
              <th className="w-10 px-4 py-3">
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected && !allSelected}
                  onChange={(e) => onSelectAll?.(e.target.checked)}
                />
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Lead Info
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Skills & Experience
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Source
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Telecaller
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                HR
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Created
              </th>
              <th className="w-20 px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {leads.map((lead, index) => (
              <tr key={lead?.id || `lead-${index}`} className="hover:bg-muted/50">
                <td className="px-4 py-4">
                  <Checkbox
                    checked={selectedLeads.includes(lead.id)}
                    onChange={(e) => onLeadSelect?.(lead.id, e.target.checked)}
                  />
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center gap-3">
                    <div className="flex-shrink-0 h-10 w-10">
                      <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-sm font-medium text-primary">
                          {lead?.name?.charAt(0)?.toUpperCase() || '?'}
                        </span>
                      </div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground truncate">
                        {lead?.name || 'N/A'}
                      </p>
                      <p className="text-sm text-muted-foreground truncate">
                        {lead?.email || lead?.phone}
                      </p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <div>
                    <div className="flex flex-wrap gap-1 mb-1">
                      {(() => {
                        const skills = parseSkills(lead?.skills);
                        return skills.slice(0, 2).map((skill, index) => (
                          <span
                            key={`${lead.id}-skill-${index}-${skill}`}
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary/10 text-primary"
                          >
                            {skill}
                          </span>
                        ));
                      })()}
                      {(() => {
                        const skills = parseSkills(lead?.skills);
                        return skills.length > 2 && (
                          <span className="text-xs text-muted-foreground">
                            +{skills.length - 2} more
                          </span>
                        );
                      })()}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {lead?.experience_years || 0} years • {lead?.skill_level || 'entry'}
                    </p>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(lead?.status)}`}>
                    {formatStatus(lead?.status)}
                  </span>
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center gap-2">
                    <Icon name={getSourceIcon(lead?.source)} size={14} className="text-muted-foreground" />
                    <span className="text-sm text-foreground">
                      {formatSource(lead?.source)}
                    </span>
                  </div>
                </td>
                <td className="px-4 py-4">
                  <p className="text-sm text-foreground">
                    {getTelecallerName(lead?.assigned_telecaller_id)}
                  </p>
                </td>
                <td className="px-4 py-4">
                  <p className="text-sm text-foreground">
                    {lead?.assigned_hr?.full_name || '-'}
                  </p>
                </td>
                <td className="px-4 py-4">
                  <p className="text-sm text-muted-foreground">
                    {lead?.created_at ? new Date(lead.created_at).toLocaleDateString() : 'N/A'}
                  </p>
                </td>
                <td className="px-4 py-4">
                  <div className="flex items-center gap-1">
                    {lead?.resume_url && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => window.open(getFileUrl(lead?.resume_url), '_blank')}
                        className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700"
                        title="View Resume"
                      >
                        <Icon name="FileText" size={14} />
                      </Button>
                    )}
                    {onMoveToATS && (['validated', 'shortlisted', 'sent_to_client'].includes(lead?.status) || lead?.lead_calls?.some(c => c.is_valid_lead === true || c.is_valid_lead === 1 || c.is_valid_lead === '1')) && lead?.status !== 'moved_to_ats' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onMoveToATS?.(lead)}
                        className="h-8 w-8 p-0 text-primary hover:text-primary"
                        title="Move to ATS"
                      >
                        <Icon name="ArrowRight" size={14} />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onViewLead?.(lead)}
                      className="h-8 w-8 p-0"
                      title="View Details"
                    >
                      <Icon name="Eye" size={14} />
                    </Button>
                    {onEditLead && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onEditLead?.(lead)}
                        className="h-8 w-8 p-0"
                        title="Edit Lead"
                      >
                        <Icon name="Edit" size={14} />
                      </Button>
                    )}
                    {onDeleteLead && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          if (window.confirm(`Are you sure you want to delete lead "${lead?.name}"?`)) {
                            onDeleteLead?.(lead.id);
                          }
                        }}
                        className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                        title="Delete Lead"
                      >
                        <Icon name="Trash2" size={14} />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="lg:hidden">
        <div className="p-4 border-b border-border bg-muted/50">
          <div className="flex items-center justify-between">
            <Checkbox
              checked={allSelected}
              indeterminate={someSelected && !allSelected}
              onChange={(e) => onSelectAll?.(e.target.checked)}
              label={`Select All (${leads.length})`}
            />
          </div>
        </div>

        <div className="divide-y divide-border">
          {leads.map((lead, index) => (
            <div key={lead?.id || `lead-${index}`} className="p-4">
              <div className="flex items-start gap-3">
                <Checkbox
                  checked={selectedLeads.includes(lead.id)}
                  onChange={(e) => onLeadSelect?.(lead.id, e.target.checked)}
                  className="mt-1"
                />

                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-xs font-medium text-primary">
                          {lead?.name?.charAt(0)?.toUpperCase() || '?'}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium text-foreground">{lead?.name}</p>
                        <p className="text-sm text-muted-foreground">{lead?.phone}</p>
                      </div>
                    </div>

                    <div className="flex items-center">
                      {lead?.resume_url && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(getFileUrl(lead?.resume_url), '_blank')}
                          className="h-8 w-8 p-0 text-blue-600 mr-1"
                        >
                          <Icon name="FileText" size={14} />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onViewLead?.(lead)}
                        className="h-8 w-8 p-0"
                      >
                        <Icon name="Eye" size={14} />
                      </Button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getStatusColor(lead?.status)}`}>
                        {formatStatus(lead?.status)}
                      </span>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground">
                        <Icon name={getSourceIcon(lead?.source)} size={12} />
                        {formatSource(lead?.source)}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-1">
                      {(() => {
                        const skills = parseSkills(lead?.skills);
                        return skills.slice(0, 3).map((skill, index) => (
                          <span
                            key={`${lead.id}-skill-${index}-${skill}`}
                            className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-primary/10 text-primary"
                          >
                            {skill}
                          </span>
                        ));
                      })()}
                      {(() => {
                        const skills = parseSkills(lead?.skills);
                        return skills.length > 3 && (
                          <span className="text-xs text-muted-foreground">
                            +{skills.length - 3}
                          </span>
                        );
                      })()}
                    </div>

                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>{lead?.experience_years || 0} years • {lead?.skill_level}</span>
                      <span>{getTelecallerName(lead?.assigned_telecaller_id)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default LeadTable;